import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AccountIconName } from '../shared/account-icon.component';
import { ProfilePictureService } from '../services/profile-picture.service';

export interface ProfileMenuUser {
  firstName?: string;
  lastName?: string;
  email?: string;
  profile?: string | null;
}

interface ProfileMenuItem {
  key: 'profile' | 'security' | 'settings';
  label: string;
  hint: string;
  icon: AccountIconName;
  _uiVisible: boolean;
}

@Component({
  selector: 'app-profile-menu',
  templateUrl: './profile-menu.component.html',
  styleUrls: ['./profile-menu.component.scss'],
  standalone: false,
})
export class ProfileMenuComponent implements OnInit, OnChanges, OnDestroy {
  @Input() user: ProfileMenuUser | null = null;
  /** Empty on Switch Account, where no supplier is picked: links then go to the site-less /account pages. */
  @Input() siteId = '';
  @Input() siteName = '';
  @Input() settingUrl = '';
  @Input() hasSettingsPage = false;
  @Output() signOut = new EventEmitter<void>();

  isOpen = false;
  displayName = '';
  displayEmail = '';
  initial = '';
  pictureUrl: string | null = null;
  items: ProfileMenuItem[] = [];
  private pictureSubscription: Subscription;

  constructor(private router: Router, private pictureService: ProfilePictureService) {}

  // The header avatar shows the real profile picture, kept in sync with uploads on User details.
  ngOnInit(): void {
    this.pictureSubscription = this.pictureService.picture$.subscribe((url) => (this.pictureUrl = url));
    this.pictureService.load();
  }

  ngOnDestroy(): void {
    this.pictureSubscription?.unsubscribe();
  }

  ngOnChanges(): void {
    const firstName = this.user?.firstName || '';
    const lastName = this.user?.lastName || '';
    this.displayName = `${firstName} ${lastName}`.trim();
    this.displayEmail = this.user?.email || '';
    this.initial = firstName.charAt(0).toUpperCase();
    this.items = [
      { key: 'profile', label: 'User Profile', hint: 'Details, access and signed terms', icon: 'user', _uiVisible: true },
      { key: 'security', label: 'Security', hint: 'Sign-in methods and devices', icon: 'shield', _uiVisible: true },
      { key: 'settings', label: 'Settings', hint: this.siteName || '', icon: 'settings', _uiVisible: this.hasSettingsPage },
    ];
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
  }

  close(): void {
    this.isOpen = false;
  }

  activate(item: ProfileMenuItem): void {
    if (item.key === 'settings') {
      this.close();
      this.router.navigateByUrl(this.settingUrl);
      return;
    }
    this.goTo(item.key === 'profile' ? 'user-profile/details' : 'security/authentication');
  }

  goTo(path: string): void {
    this.close();
    const base = this.siteId ? `/supplier/${this.siteId}` : '/account';
    this.router.navigateByUrl(`${base}/${path}`);
  }

  onSignOut(): void {
    this.close();
    this.signOut.emit();
  }
}
